import { createContext, useContext, useEffect, useState, useCallback } from 'react';
import {
  getNotifications,
  markNotificationRead,
  markAllNotificationsRead,
  dismissNotification,
  type NotificationItem,
} from '@/services/notificationService';

interface NotificationsContextValue {
  items: NotificationItem[];
  unreadCount: number;
  loading: boolean;
  refresh: () => Promise<void>;
  markRead: (id: string) => Promise<void>;
  markAllRead: () => Promise<void>;
  dismiss: (id: string) => Promise<void>;
}

const NotificationsContext = createContext<NotificationsContextValue | null>(null);

const POLL_MS = 60000;

export function NotificationsProvider({ children }: { children: React.ReactNode }) {
  const [items, setItems] = useState<NotificationItem[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const res = await getNotifications();
      setItems(res.data || []);
      setUnreadCount(res.unreadCount || 0);
    } catch {
      // No access or transient error — stay quiet rather than break the UI.
      setItems([]);
      setUnreadCount(0);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
    const t = setInterval(refresh, POLL_MS);
    return () => clearInterval(t);
  }, [refresh]);

  const markRead = useCallback(async (id: string) => {
    setItems((prev) => prev.map((x) => (x.id === id ? { ...x, isRead: true } : x)));
    setUnreadCount((u) => {
      const wasUnread = items.find((x) => x.id === id && !x.isRead);
      return wasUnread ? Math.max(0, u - 1) : u;
    });
    try { await markNotificationRead(id); } catch { /* optimistic */ }
  }, [items]);

  const markAllRead = useCallback(async () => {
    setItems((prev) => prev.map((x) => ({ ...x, isRead: true })));
    setUnreadCount(0);
    try { await markAllNotificationsRead(); } catch { refresh(); }
  }, [refresh]);

  const dismiss = useCallback(async (id: string) => {
    const wasUnread = items.find((x) => x.id === id && !x.isRead);
    setItems((prev) => prev.filter((x) => x.id !== id));
    if (wasUnread) setUnreadCount((u) => Math.max(0, u - 1));
    try { await dismissNotification(id); } catch { refresh(); }
  }, [items, refresh]);

  return (
    <NotificationsContext.Provider
      value={{ items, unreadCount, loading, refresh, markRead, markAllRead, dismiss }}
    >
      {children}
    </NotificationsContext.Provider>
  );
}

export function useNotifications() {
  const ctx = useContext(NotificationsContext);
  if (!ctx) {
    throw new Error('useNotifications must be used within a NotificationsProvider');
  }
  return ctx;
}
